import type { PaintingRecord, TrackRecord } from "../types";
import { escapeHtml } from "../util/html";
import { resolveAudioUrl } from "../util/audio";
import { fetchJson } from "../util/fetch-json";

const TRACKS_DATA_URL = "/data/tracks-local.json";
const PAINTINGS_DATA_URL = "/data/paintings.json";

/** 北京时区日历日键 yyyy-mm-dd，用于「今日一曲 / 今日一画」与全球访客同一天内推荐一致 */
export function chinaDayKey(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** @deprecated 使用 {@link chinaDayKey} 做首页每日推荐 */
export function localDayKey(): string {
  return chinaDayKey();
}

/** 同一日期、同一盐值下稳定落在 [0, n) */
export function stablePickIndex(dayKey: string, salt: string, n: number): number {
  if (n <= 0) return 0;
  const s = `${dayKey}\0${salt}`;
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h >>> 0) % n;
}

function trunc(s: string, max: number): string {
  const t = s.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  return t.slice(0, max - 1) + "…";
}

export function renderHome(): string {
  return `
    <section class="hero">
      <div>
        <p class="hero-kicker">古典音乐 × 古典名画</p>
        <h1 class="hero-title">在一座线上空间里，听懂名曲、读懂名画</h1>
        <p class="hero-lead">
          尚诺艾将曲库、讲解、猜歌与名画虚拟馆藏统一策展；为小众爱好者提供可反复访问的深度内容，
          并与微信小程序形成联动，把「欣赏」延伸到指尖与日常。
        </p>
      </div>
      <div class="hero-panel">
        <h2>四大馆区</h2>
        <div class="pillar-grid pillar-grid-four">
          <button type="button" class="pillar-card music" data-route="/music">
            <strong>名曲馆</strong>
            <span>聆听、讲解 — 以曲目为核心的学习路径</span>
          </button>
          <button type="button" class="pillar-card art" data-route="/art">
            <strong>名画馆</strong>
            <span>虚拟馆藏、高清细节与画家故事</span>
          </button>
          <button type="button" class="pillar-card pair" data-route="/pair">
            <strong>对对配馆</strong>
            <span>以名画为主，策展画风相近的相配名曲</span>
          </button>
          <button type="button" class="pillar-card play" data-route="/games">
            <strong>互动游戏</strong>
            <span>测歌名等；名画对对配与音乐线条在微信小程序</span>
          </button>
        </div>
      </div>
    </section>

    <section class="daily-picks" aria-labelledby="daily-picks-title">
      <div class="daily-picks-header">
        <h2 id="daily-picks-title" class="section-title daily-picks-title">今日一曲 · 今日一画</h2>
        <p id="daily-picks-date" class="daily-picks-date" aria-live="polite"></p>
      </div>
      <p class="section-sub daily-picks-lead">
        每天按<strong>北京时间日期</strong>换一组推荐，同一天内各位访客看到相同一曲、一画；跨自然日自动更换。欢迎把本站加入书签（Windows <kbd class="daily-kbd">Ctrl</kbd>+<kbd class="daily-kbd">D</kbd>，Mac <kbd class="daily-kbd">⌘</kbd>+<kbd class="daily-kbd">D</kbd>），明天再过来看看。
      </p>
      <div id="daily-picks-grid" class="daily-picks-grid daily-picks-placeholder">
        <p class="section-sub daily-picks-hint">
          为加快首页打开，此处<strong>不自动请求</strong>曲库与画作 JSON。请<strong>向下滚动到本区域</strong>或点击按钮后再加载「今日一曲 · 今日一画」。
        </p>
        <button type="button" class="pill-btn" id="daily-picks-load">加载今日推荐</button>
      </div>
    </section>

    <h2 class="section-title">今日策展 · 入门三联</h2>
    <p class="section-sub">快速体验「听—看—配—读」：<a href="/music">曲库</a> · <a href="/art">名画馆</a> · <a href="/pair">对对配馆</a> · <a href="/guides">导读</a> · <a href="/games">互动游戏</a>。</p>
    <div class="tag-row">
      <span class="tag">每日更新</span>
      <span class="tag">策展式浏览</span>
      <span class="tag">可收藏常访</span>
    </div>
  `;
}

function dailyTrackCard(t: TrackRecord): string {
  const meta = [t.composer, t.era, t.year != null ? String(t.year) : ""].filter(Boolean).join(" · ");
  const blurb = trunc(t.introBlurb || t.story || "", 140);
  const src = t.audioFile ? escapeHtml(resolveAudioUrl(t.audioFile)) : "";
  const player = src
    ? `<div class="daily-card-audio"><audio controls preload="none" src="${src}"></audio></div>`
    : "";
  return `
    <article class="daily-card daily-card--music">
      <p class="daily-card-eyebrow">今日一曲</p>
      <h3 class="daily-card-title"><a href="/music?track=${t.id}">${escapeHtml(t.title)}</a></h3>
      <p class="daily-card-meta">${escapeHtml(meta)}</p>
      ${blurb ? `<p class="daily-card-blurb">${escapeHtml(blurb)}</p>` : ""}
      ${player}
      <a class="daily-card-cta" href="/music?track=${t.id}">打开作品页 →</a>
    </article>
  `;
}

function dailyPaintingCard(p: PaintingRecord): string {
  const meta = [p.artist, p.era, p.year != null ? String(p.year) : ""].filter(Boolean).join(" · ");
  const blurb = trunc(p.intro || "", 120);
  const img = p.imageUrl ? escapeHtml(p.imageUrl) : "";
  const visual = img
    ? `<div class="daily-card-visual"><img src="${img}" alt="${escapeHtml(p.title)}" loading="lazy" decoding="async" width="480" height="360" /></div>`
    : "";
  return `
    <article class="daily-card daily-card--art">
      <p class="daily-card-eyebrow">今日一画</p>
      ${visual}
      <h3 class="daily-card-title"><a href="/art?painting=${p.id}">${escapeHtml(p.title)}</a></h3>
      <p class="daily-card-meta">${escapeHtml(meta)}</p>
      ${blurb ? `<p class="daily-card-blurb">${escapeHtml(blurb)}</p>` : ""}
      <a class="daily-card-cta" href="/art?painting=${p.id}">打开画作页 →</a>
    </article>
  `;
}

export function hydrateHome(container: HTMLElement): void {
  const dateEl = container.querySelector("#daily-picks-date");
  const grid = container.querySelector("#daily-picks-grid");
  const section = container.querySelector(".daily-picks");
  if (!grid || !section) return;

  if (dateEl) {
    dateEl.textContent = new Date().toLocaleDateString("zh-CN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "Asia/Shanghai",
    });
  }

  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    if (grid.isConnected) {
      grid.innerHTML = '<p class="section-sub daily-picks-loading">正在加载今日推荐…</p>';
    }
    void loadDailyPicks(container);
  };

  grid.querySelector("#daily-picks-load")?.addEventListener("click", start);

  if (typeof IntersectionObserver !== "undefined") {
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          start();
          io.disconnect();
        }
      },
      { root: null, rootMargin: "200px 0px", threshold: 0.01 }
    );
    io.observe(section);
  }
}

async function loadDailyPicks(container: HTMLElement): Promise<void> {
  const grid = container.querySelector("#daily-picks-grid");
  if (!grid) return;

  try {
    const [tracksPayload, paintingsPayload] = await Promise.all([
      fetchJson<{ tracks?: TrackRecord[] }>(TRACKS_DATA_URL, "曲库"),
      fetchJson<{ paintings?: PaintingRecord[] }>(PAINTINGS_DATA_URL, "名画目录"),
    ]);

    if (!grid.isConnected) return;

    const tracks = tracksPayload.tracks ?? [];
    const paintings = paintingsPayload.paintings ?? [];

    const dayKey = chinaDayKey();
    const ti = stablePickIndex(dayKey, "sanoi:daily-track", tracks.length);
    const pi = stablePickIndex(dayKey, "sanoi:daily-painting", paintings.length);

    const parts: string[] = [];

    if (tracks.length > 0) {
      parts.push(dailyTrackCard(tracks[ti]!));
    } else {
      parts.push(`<article class="daily-card daily-card--empty daily-card--music">
        <p class="daily-card-eyebrow">今日一曲</p>
        <p class="daily-card-blurb">曲库暂未就绪。请稍后在<a href="/music">名曲馆</a>浏览全库。</p>
      </article>`);
    }

    if (paintings.length > 0) {
      parts.push(dailyPaintingCard(paintings[pi]!));
    } else {
      parts.push(`<article class="daily-card daily-card--empty daily-card--art">
        <p class="daily-card-eyebrow">今日一画</p>
        <p class="daily-card-blurb">馆藏画作数据暂未就绪。请稍后在<a href="/art">名画馆</a>浏览。</p>
      </article>`);
    }

    if (grid.isConnected) grid.innerHTML = parts.join("");
  } catch {
    if (grid.isConnected) {
      grid.innerHTML =
        '<p class="section-sub">暂时无法加载今日推荐，请稍后再试。</p>';
    }
  }
}
